import { useState, useEffect } from "react";
import { X, MessageCircle, ArrowRight } from "lucide-react";
import { SPEC_ROWS } from "../data/products";
import { buildWhatsAppLink } from "../data/config";

export const ProductModal = ({ product, content, onClose, onRegister }) => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    setActive(0);
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [product, onClose]);

  if (!product) return null;

  const waLink = buildWhatsAppLink(
    `Hi evLUXE, I am interested in ${product.name}. Please share details.`,
    content.contact.whatsappNumber
  );

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={`${product.name} details`}
      data-testid="product-modal"
    >
      <div
        className="absolute inset-0 bg-black/82 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative glass w-full sm:max-w-5xl h-[92svh] sm:h-auto sm:max-h-[92vh] overflow-y-auto overflow-x-hidden rounded-t-lg sm:rounded-lg border border-white/10">
        <button
          onClick={onClose}
          data-testid="modal-close"
          aria-label="Close"
          className="sticky top-3 float-right mr-3 z-20 bg-white/12 backdrop-blur-md hover:bg-brand hover:text-black text-white rounded-full p-2.5 transition-colors"
        >
          <X size={20} />
        </button>

        <div className="p-4 pt-5 sm:p-8 grid lg:grid-cols-[1.08fr_0.92fr] gap-5 sm:gap-8 clear-both">
          <div className="min-w-0">
            <div className="image-stage product-stage rounded-lg p-1.5 sm:p-2 flex items-center justify-center aspect-[1.2/1] sm:aspect-[4/3]">
              <img
                src={product.images[active]}
                alt={`evLUXE ${product.name} electric scooter in ${
                  product.colorNames?.[active] || `colour ${active + 1}`
                }`}
                className="h-full w-full object-contain"
              />
            </div>
            {product.images.length > 1 && (
              <div className="mt-3 sm:mt-4">
                <div className="mb-2 flex items-center justify-between gap-3 text-xs">
                  <span className="text-white/50">{content.actions.availableColours}</span>
                  <span className="font-mono text-brand">{product.colorNames?.[active]}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {product.images.map((img, i) => (
                    <button
                      key={img}
                      onClick={() => setActive(i)}
                      data-testid={`modal-thumb-${i}`}
                      aria-label={`${product.name} in ${product.colorNames?.[i] || `colour ${i + 1}`}`}
                      title={product.colorNames?.[i]}
                      className={`relative h-16 sm:h-20 bg-surface rounded-lg p-1.5 sm:p-2 border transition-colors ${
                        active === i ? "border-brand" : "border-white/10 hover:border-white/30"
                      }`}
                    >
                      <img src={img} alt="" className="h-full w-full object-contain" loading="lazy" />
                      <span
                        aria-hidden="true"
                        className={`absolute bottom-1.5 left-1/2 h-1 w-8 -translate-x-1/2 rounded-full transition-colors ${
                          active === i ? "bg-brand" : "bg-white/20"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="min-w-0">
            <p className="font-mono text-xs text-brand">{product.series}</p>
            <h3 className="font-display font-bold text-2xl sm:text-3xl mt-1">{product.name}</h3>
            <p className="text-white/60 mt-2 text-sm leading-relaxed">{product.description}</p>

            <div className="mt-5 divide-y divide-white/5 border border-white/10 rounded-lg overflow-hidden">
              {SPEC_ROWS.map((row) => (
                <div key={row.key} className="grid grid-cols-[0.82fr_1.18fr] gap-3 px-3 sm:px-4 py-2.5 text-sm">
                  <span className="text-white/50 min-w-0">{content.productLabels[row.key] || row.label}</span>
                  <span className="text-white/90 font-mono text-right min-w-0 break-words leading-snug">
                    {product.specs[row.key]}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-3">
              <button
                onClick={() => {
                  onRegister(product.name);
                  onClose();
                }}
                data-testid="modal-register-cta"
                className="btn-glow inline-flex items-center justify-center gap-2 bg-brand text-black font-semibold px-6 py-3 rounded-full"
              >
                {content.actions.registerInterest} <ArrowRight size={16} />
              </button>
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="modal-whatsapp-cta"
                className="inline-flex items-center justify-center gap-2 border border-brand/50 text-brand hover:bg-brand hover:text-black px-6 py-3 rounded-full transition-colors"
              >
                <MessageCircle size={16} /> {content.actions.enquireWhatsApp}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;
